import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { commonStrings, userStrings } from 'constants/strings'
import { Button } from 'components/Button'
import { AdCodeBadge } from 'components/AdCodeBadge'
import { TagChip } from 'components/TagChip'
import type { TLineFriendListItem } from 'types'
import { useNavigate } from 'react-router-dom'
import { useOrgPath } from 'hooks/useOrgPath'
import {
  buildUserEditPath,
  formatLineFriendBasicId,
  formatLineFriendDateTime,
  getFriendStatusLabel,
} from './lineFriendView'
import { TestUserSwitch } from './TestUserSwitch'

type UsersTableProps = {
  users: TLineFriendListItem[]
  loading: boolean
  emptyMessage?: string
  onToggleTestUser: (userLineId: number, isTestUser: boolean) => void
  pendingTestUserIds: number[]
}

const COLUMN_COUNT = 10

export function UsersTable({
  users,
  loading,
  emptyMessage = userStrings.states.empty,
  onToggleTestUser,
  pendingTestUserIds,
}: UsersTableProps) {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const handleEdit = (userLineId: number) =>
    navigate(orgPathFor(buildUserEditPath(userLineId)))

  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = <DataTableMessageRow colSpan={COLUMN_COUNT} message={commonStrings.loading} />
  } else if (users.length === 0) {
    body = <DataTableMessageRow colSpan={COLUMN_COUNT} message={emptyMessage} />
  } else {
    body = users.map((user) => (
      <UsersTableRow
        key={user.id}
        user={user}
        onEdit={handleEdit}
        onToggleTestUser={onToggleTestUser}
        isTestUserPending={pendingTestUserIds.includes(user.id)}
      />
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1160px]"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.actions}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.id}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.lineName}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.linkedBasicId}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.friendStatus}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.tag}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.isTestUser}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.filters.adCode}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.lastAccessAt}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {userStrings.table.headers.friendRegisteredAt}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

type UsersTableRowProps = {
  user: TLineFriendListItem
  onEdit: (userLineId: number) => void
  onToggleTestUser: (userLineId: number, isTestUser: boolean) => void
  isTestUserPending: boolean
}

function UsersTableRow({
  user,
  onEdit,
  onToggleTestUser,
  isTestUserPending,
}: UsersTableRowProps) {
  const emptyValue = userStrings.edit.summary.emptyValue
  const linkedBasicId =
    formatLineFriendBasicId(user.linkedBasicId) ?? userStrings.table.noLinkedBasicId
  const friendStatus = getFriendStatusLabel(user.friendStatus)
  const lastAccessAt = formatLineFriendDateTime(user.lastAccessAt) ?? emptyValue
  const friendRegisteredAt =
    formatLineFriendDateTime(user.friendRegisteredAt) ?? emptyValue

  return (
    <tr className="border-t border-border/60 bg-white">
      <td className="px-2 py-3 text-left">
        <Button
          type="button"
          size="sm"
          onClick={() => onEdit(user.id)}
        >
          {commonStrings.actions.edit}
        </Button>
      </td>
      <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
        {user.id}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {user.lineName}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {linkedBasicId}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {friendStatus}
      </td>
      <td className="px-2 py-3">
        {user.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {user.tags.map((tag) => (
              <TagChip
                key={tag.id}
                name={tag.name}
                color={tag.color}
                variant="solid"
              />
            ))}
          </div>
        ) : (
          <span className="text-[11px] text-muted sm:text-sm">
            {userStrings.table.emptyValue}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <TestUserSwitch
          checked={user.isTestUser}
          disabled={isTestUserPending}
          ariaLabel={`${userStrings.table.headers.isTestUser} ${user.id}`}
          onChange={(next) => onToggleTestUser(user.id, next)}
        />
      </td>
      <td className="px-2 py-3">
        {user.adCode ? (
          <AdCodeBadge
            name={user.adCode.name}
            confidence={user.adCode.confidence}
          />
        ) : (
          <span className="text-[11px] text-muted sm:text-sm">
            {userStrings.table.emptyValue}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {lastAccessAt}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {friendRegisteredAt}
      </td>
    </tr>
  )
}
